import { useId, useState } from 'react'
import { formatCurrency } from '../utils/formatters.js'
import {
  AGENT_FEE_RANGE,
  CALC_PRICE_MAX,
  CALC_PRICE_MIN,
  CALC_PRICE_STEP,
  TYPICAL_AGENT_FEE_PCT,
  buildSavingsBreakdown,
  clampCalcPrice,
} from '../utils/sellGuide.js'
import './SavingsCalculator.css'

/**
 * "What would an estate agent cost you?" calculator.
 *
 * The single most persuasive element on a seller landing page is a concrete
 * pound figure the seller chose themselves, so this is interactive: the reader
 * sets their own asking price and sees the commission disappear.
 *
 * The static crawler HTML for pages using this is produced at build time by
 * scripts/render-sell-pages.mjs (`savingsCalculatorMarkup`) — it emits the same
 * structure frozen at the town's median price. Keep the two in sync: class names
 * and copy are asserted by e2e/prerender-seo.spec.js. The static markup is inert
 * (no inline JS); React replaces it on mount via createRoot, which is where the
 * interactivity comes from.
 *
 * @param {object} props
 * @param {number} [props.initialPrice] - starting asking price (a town median)
 * @param {string} [props.areaName] - town name, used to localise the heading
 */
const SavingsCalculator = ({ initialPrice = 250000, areaName = '' }) => {
  const priceInputId = useId()
  const [price, setPrice] = useState(() => clampCalcPrice(initialPrice))

  const { rows } = buildSavingsBreakdown(price)
  const typicalFee = rows.find((r) => r.pct === TYPICAL_AGENT_FEE_PCT)?.fee ?? 0
  const heading = areaName
    ? `What would an estate agent cost you in ${areaName}?`
    : 'What would an estate agent cost you?'

  const handleChange = (event) => {
    // Keep the raw value while typing so the field does not fight the user;
    // clamp only when it is a usable number.
    const raw = event.target.value
    setPrice(raw === '' ? '' : clampCalcPrice(raw, price || 250000))
  }

  const handleBlur = () => setPrice((current) => clampCalcPrice(current))

  return (
    <section className="savings-calc" aria-labelledby={`${priceInputId}-heading`}>
      <div className="savings-calc__head">
        <h2 id={`${priceInputId}-heading`}>{heading}</h2>
        <p>
          Set your asking price and see what you would hand over in commission — and what you keep by
          selling on Keyzee instead.
        </p>
      </div>

      <div className="savings-calc__controls">
        <label className="savings-calc__label" htmlFor={priceInputId}>
          Your asking price
        </label>
        <div className="savings-calc__price-row">
          <span className="savings-calc__currency" aria-hidden="true">
            £
          </span>
          <input
            id={priceInputId}
            className="savings-calc__number"
            type="number"
            inputMode="numeric"
            min={CALC_PRICE_MIN}
            max={CALC_PRICE_MAX}
            step={CALC_PRICE_STEP}
            value={price}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
        <input
          className="savings-calc__slider"
          type="range"
          aria-label="Asking price"
          min={CALC_PRICE_MIN}
          max={CALC_PRICE_MAX}
          step={CALC_PRICE_STEP}
          value={clampCalcPrice(price)}
          onChange={(event) => setPrice(Number(event.target.value))}
        />
      </div>

      <table className="savings-calc__table">
        <caption className="savings-calc__caption">
          Estimated agent commission on {formatCurrency(clampCalcPrice(price))}
        </caption>
        <thead>
          <tr>
            <th scope="col">Agent fee band</th>
            <th scope="col">You would pay</th>
            <th scope="col">On Keyzee</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.pct}
              className={row.pct === TYPICAL_AGENT_FEE_PCT ? 'is-typical' : undefined}
            >
              <th scope="row">{row.label}</th>
              <td className="savings-calc__fee">{formatCurrency(row.fee)}</td>
              <td className="savings-calc__free">£0</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="savings-calc__result">
        At a typical {TYPICAL_AGENT_FEE_PCT}% fee that is <strong>{formatCurrency(typicalFee)}</strong> in
        commission. On Keyzee it is <strong>£0</strong> — you keep the lot.
      </p>

      <p className="savings-calc__note">
        Estimates only, for illustration. Agent fees vary by area, agent and property; most quote a
        percentage plus VAT. Keyzee charges no listing fee and no commission.
      </p>
    </section>
  )
}

export default SavingsCalculator
